import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { Row, Col, Card, Table, Pagination, Button, Badge } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import RoundedPagination from "../../Components/Paginate";
import FormInput from "../../Components/FormInput";
import { Link, useNavigate } from "react-router-dom";
import swal from 'sweetalert2';
import { toast } from "react-toastify";
import { logout } from "../../redux/actions/AuthActions";
import useDebounce from "../../hooks/useDebounce";
import { getDisplayDateTimeFormat } from '../../util/generalUtills'
import { deleteScheduleBookingData, getScheduleBookingList, updateScheduleBookingData, updateScheduleBookingStatusData } from "../../redux/actions/ScheduleBookingActions";
import Preloader from "../../Components/Preloader";
import Status from "../../enums/Status";



const Dashboard = ({ status, title }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    AuthReducer: { auth, errors, loading },
    scheduleBookingReducer: {
      scheduleBookingLists,
      scheduleBookingCount,
      scheduleBookingPermissions,
      loadingScheduleBooking,
      errorsScheduleBooking,
      changedScheduleBookingStatus
    },
  } = useSelector((state) => state);
  const [page, setPage] = useState(1);
  const [paginate, setPaginate] = useState(100);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState('desc');
  const indexOfLastItem = (page + 1) * paginate;
  const indexOfFirstItem = indexOfLastItem - paginate;


  const fetchDataList = useCallback(() => {
    if (auth?.accessToken) {
      dispatch(getScheduleBookingList(auth?.accessToken, paginate, page, search, sort));
    }
  }, [auth?.accessToken, paginate, page, search, sort, dispatch]);

  useDebounce(fetchDataList, 500, [auth, paginate, page, search, sort]);


  useEffect(() => {
    if (errors) {
      if (errors?.status === 401) {
        dispatch(logout())
        toast.error(errors?.data?.message);
        navigate('/login');
        window.location.reload();
      }
    }
  }, [errors])


  const handlePaginateChange = (e) => {
    const value = e.target.value === "All" ? scheduleBookingCount : parseInt(e.target.value);
    setPaginate(value);
    setPage(1);
  };


  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };


  const handleDeleteRequest = (id) => {
    swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28bb4b",
      cancelButtonColor: "#f34e4e",
      confirmButtonText: "Yes, delete it!",
    })
      .then((result) => {
        if (result.isConfirmed) {
          dispatch(deleteScheduleBookingData(auth?.accessToken, id))
        }
      });
  };

  const handleStatusChangeRequest = (item, newStatus) => {
    swal.fire({
      title: "Are you sure?",
      text: `Do you want to change the status to ${newStatus}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28bb4b",
      cancelButtonColor: "#f34e4e",
      confirmButtonText: "Yes, change it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(updateScheduleBookingData(auth?.accessToken, {jenId: item?.jenId,
          mobile: item?.mobile,
          name: item?.name,
          altNumber: item?.altNumber,
          bookingDate: item?.bookingDate,
          bookingNumber:item?.bookingNumber,
          vehicleNumber: item?.vehicleNumber,
          hydrantId: item?.hydrantId,
             hydrantName:item?.hydrantName,
          destinationName:item?.destinationName,
          destinationId: item?.destinationId,
          hydrantLat: item?.hydrantLat,
          hydrantLng: item?.hydrantLng,
          destinationLat: item?.destinationLat,
          destinationLng: item?.destinationLng,
          totalDistance: Number(item?.totalDistance),
          sarpanchId: item?.sarpanchId,
          blockId: item?.blockId,
          sunday:Number( item?.sunday),
          monday:Number( item?.monday),
          tuesday: Number(item?.tuesday),
          wednesday: Number(item?.wednesday),
          thursday: Number(item?.thursday),
          friday: Number(item?.friday),
          saturday: Number(item?.saturday),
          status: newStatus,

        },item?.id));
      }
    });
  };


  useEffect(() => {
    if (changedScheduleBookingStatus?.message) {
      swal.fire({
        show: true,
        title: "Great !",
        text: changedScheduleBookingStatus.message,
        icon: "success",
        showConfirmButton: false,
        timer: 1500
      });
      dispatch({ type: 'RESET_DELETE_SCHEDULE_BOOKING_STATUS' });
      dispatch(getScheduleBookingList(auth?.accessToken, paginate, page, search, sort));
    }
  }, [changedScheduleBookingStatus])


  useEffect(() => {
    if (errorsScheduleBooking?.status === 401) {
      dispatch(logout())
      toast.error(errors?.data?.message);
      navigate('/login');
    }
    else if (errorsScheduleBooking?.status === 400) {
      //dispatch({ type: 'RESET_DELETE_STATUS' });
    }
  }, [errorsScheduleBooking])

  const tableRef = useRef();

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: "Schedule Booking", path: "/Schedule-Booking", active: true },
        ]}
        title={t("Schedule Booking")}
      />

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <div className="mb-2">
                <Row>
                  <Col lg={3}>
                    {(scheduleBookingPermissions?.filter) && (
                      <>
                        <div className="d-flex align-items-center">
                          <input
                            type="search"
                            placeholder="Search..."
                            className="form-control ms-1"
                            autoComplete="off"
                            value={search}
                            onChange={handleSearchChange}
                          />
                        </div>
                      </>
                    )}
                  </Col>
                  <Col lg={9}>
                    <div className="d-flex justify-content-end align-items-center">
                      <div className="button-list">
                        {(scheduleBookingPermissions?.add) && (
                          <>
                            <Link to="/Add-Schedule-Booking">
                              <Button variant="success">
                                <i className="mdi mdi-clipboard-plus-outline me-1"></i>Add
                              </Button>
                            </Link>
                          </>
                        )}
                        {(scheduleBookingPermissions?.print) && (
                          <>
                            <Button variant="dark">
                              <i className="mdi mdi-printer me-1"></i>Print
                            </Button>
                          </>
                        )}
                        {(scheduleBookingPermissions?.excel) && (
                          <>
                            <Button variant="info">
                              <i className="mdi mdi-file-excel me-1"></i>Excel
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div style={{ overflowY: 'auto', maxHeight: '500px', border: "1px solid #dee2e6" }}>
                <Table ref={tableRef} className="mb-0 table-bordered"
                  size="sm"
                  striped
                  responsive
                  id="table-to-print"
                  style={{ width: "100%", tableLayout: "auto" }}>
                  <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1 }}>
                    <tr>
                      <th>SR No.</th>
                      <th>Job Id</th>
                      <th>Name<br />Number</th>
                      <th>Booking Type</th>
                      <th>Booking Time</th>
                      <th>Vehicle Type</th>
                      <th>Vehicle Number</th>
                      <th>Driver</th>
                      <th>Hydrant</th>
                      <th>Drop Location</th>
                      <th>Booked By / Date Time</th>
                      <th>Assigned By / Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleBookingLists.length === 0 ? (
                      <tr>
                        <td colSpan="6">No data found</td>
                      </tr>
                    ) : (
                      <>
                        {scheduleBookingLists?.map?.((item, index) => {
                          return (
                            <tr key={item.id}>
                              <th scope="row">{index + 1 + (page - 1) * paginate}</th>
                              <td>{item?.bookingId}</td>
                              <td>{item?.name}<br />{item?.mobile}</td>
                              <td>{item?.bookingType}</td>
                              <td>{getDisplayDateTimeFormat(item?.bookingDate)}</td>
                              <td>{item?.vehicleType}</td>
                              <td>{item?.vehicleNumber}</td>
                              <td>{item?.driver?.name}<br />{item?.driver?.mobile}</td>
                              <td>{item?.hydrant?.name}</td>
                              <td>{item?.destination?.name}</td>
                              <td>{item?.createdBy?.name}<br />{getDisplayDateTimeFormat(item?.createdAt)}</td>
                              <td>{item?.createdBy?.name}<br />{getDisplayDateTimeFormat(item?.createdAt)}</td>
                              <td>
                                {(scheduleBookingPermissions?.edit ) && (
                                  <>
                                    {item?.status === Status.ACTIVE && (
                                      <>
                                        <Link to="#" className="action-icon text-success" onClick={() => handleStatusChangeRequest(item, Status.INACTIVE)}>
                                          {" "}
                                          <i className="mdi mdi-eye"></i>
                                        </Link>
                                      </>
                                    )}
                                    {item?.status === Status.INACTIVE && (
                                      <>
                                        <Link to="#" className="action-icon text-warning" onClick={() => handleStatusChangeRequest(item, Status.ACTIVE)}>
                                          {" "}
                                          <i className="mdi mdi-eye-off"></i>
                                        </Link>
                                      </>
                                    )}

                                    <Link to={`/Update-Schedule-Booking/${item?.id}`} className="action-icon text-info">
                                      <i className="mdi mdi-square-edit-outline"></i>
                                    </Link>
                                  </>
                                )}

                                {(scheduleBookingPermissions?.delete) && (
                                  <>
                                    <Link to="#" className="action-icon text-danger" onClick={() => handleDeleteRequest(item?.id)} >
                                      {" "}
                                      <i className="mdi mdi-delete"></i>
                                    </Link>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </tbody>
                </Table>
              </div>
              <div class="mt-3 d-lg-flex align-items-center text-center pb-1">
                <div class="d-inline-block me-3">
                  <label class="me-1">Display :</label>
                  <select class="form-select d-inline-block w-auto" onChange={handlePaginateChange}>
                    <option value="10">10</option>
                    <option value="50">50</option>
                    <option selected value="100">100</option>
                    <option value="500">500</option>
                  </select>
                </div>
                <span class="me-3">Page <strong>{page}</strong> of <strong>{Math.ceil(scheduleBookingCount / paginate)}</strong> </span>
                <RoundedPagination
                  currentPage={page}
                  totalPages={Math.ceil(scheduleBookingCount / paginate)}
                  onPageChange={(pageNumber) => setPage(pageNumber)}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loadingScheduleBooking && (<Preloader />)}

    </>
  );
};

export default Dashboard;
